import { useCallback, useEffect, useRef } from "react";
import { appEvents } from "@/shared/lib/events";
import { VOICE_INTRO_START_FALLBACK_MS } from "./marcusOnboardingFlow";
import { ONBOARDING_COMPACT_GUIDE_PREFACE } from "./marcusOnboardingIntroCopy";

type PublishStartPrompt = () => void;

interface PendingPreface {
  runId: number;
  text: string;
  publishStartPrompt: PublishStartPrompt;
  activated: boolean;
  spoken: boolean;
  fallbackTimer: number | null;
}

interface UseMarcusOnboardingPrefaceParams {
  prepareVoiceForOnboardingStep: () => void;
  speakVoiceIntro: (text?: string) => boolean;
  waitForVoiceIntroStart: () => Promise<void>;
}

export function useMarcusOnboardingPreface({
  prepareVoiceForOnboardingStep,
  speakVoiceIntro,
  waitForVoiceIntroStart,
}: UseMarcusOnboardingPrefaceParams) {
  const pendingRef = useRef<PendingPreface | null>(null);
  const promptByRunRef = useRef<Map<number, PublishStartPrompt>>(new Map());
  const avatarMountedRef = useRef(false);
  const avatarReadyRef = useRef(false);

  const cancelPreface = useCallback(() => {
    const pending = pendingRef.current;
    if (pending?.fallbackTimer) window.clearTimeout(pending.fallbackTimer);
    if (pending) promptByRunRef.current.delete(pending.runId);
    pendingRef.current = null;
  }, []);

  const publishPendingStart = useCallback((pending: PendingPreface) => {
    if (pendingRef.current !== pending) return;
    if (pending.fallbackTimer) window.clearTimeout(pending.fallbackTimer);
    promptByRunRef.current.delete(pending.runId);
    pendingRef.current = null;
    pending.publishStartPrompt();
  }, []);

  const armFallback = useCallback(
    (pending: PendingPreface) => {
      if (pending.fallbackTimer) return;
      pending.fallbackTimer = window.setTimeout(
        () => publishPendingStart(pending),
        VOICE_INTRO_START_FALLBACK_MS,
      );
    },
    [publishPendingStart],
  );

  const trySpeakPreface = useCallback(
    (pending: PendingPreface | null = pendingRef.current) => {
      if (!pending || pending.spoken || !avatarReadyRef.current) return;
      pending.spoken = true;
      const started = speakVoiceIntro(pending.text);
      if (!started) {
        publishPendingStart(pending);
        return;
      }
      void waitForVoiceIntroStart().then(() => publishPendingStart(pending));
    },
    [publishPendingStart, speakVoiceIntro, waitForVoiceIntroStart],
  );

  const tryActivateAvatar = useCallback(
    (pending: PendingPreface | null = pendingRef.current) => {
      if (!pending || pending.activated || !avatarMountedRef.current) return;
      pending.activated = true;
      prepareVoiceForOnboardingStep();
      armFallback(pending);
      trySpeakPreface(pending);
    },
    [armFallback, prepareVoiceForOnboardingStep, trySpeakPreface],
  );

  useEffect(() => {
    const unsubscribeRequested = appEvents.subscribe(
      "marcus-onboarding:voice-preface-requested",
      (event) => {
        const publishStartPrompt = promptByRunRef.current.get(event.runId);
        if (!publishStartPrompt) return;
        const pending: PendingPreface = {
          runId: event.runId,
          text: event.text,
          publishStartPrompt,
          activated: false,
          spoken: false,
          fallbackTimer: null,
        };
        cancelPreface();
        avatarMountedRef.current = false;
        avatarReadyRef.current = false;
        pendingRef.current = pending;
        armFallback(pending);
        tryActivateAvatar(pending);
      },
    );
    const unsubscribeMounted = appEvents.subscribe("marcus-onboarding:compact-avatar-mounted", () => {
      avatarMountedRef.current = true;
      tryActivateAvatar();
    });
    const unsubscribeReady = appEvents.subscribe("marcus-onboarding:compact-avatar-ready", () => {
      avatarReadyRef.current = true;
      trySpeakPreface();
    });
    const unsubscribeUnavailable = appEvents.subscribe(
      "marcus-onboarding:compact-avatar-unavailable",
      () => {
        avatarMountedRef.current = true;
        const pending = pendingRef.current;
        if (pending) publishPendingStart(pending);
      },
    );

    return () => {
      unsubscribeRequested();
      unsubscribeMounted();
      unsubscribeReady();
      unsubscribeUnavailable();
      cancelPreface();
    };
  }, [armFallback, cancelPreface, publishPendingStart, tryActivateAvatar, trySpeakPreface]);

  const requestPreface = useCallback((runId: number, publishStartPrompt: PublishStartPrompt) => {
    promptByRunRef.current.set(runId, publishStartPrompt);
    appEvents.publish({
      type: "marcus-onboarding:voice-preface-requested",
      runId,
      text: ONBOARDING_COMPACT_GUIDE_PREFACE,
    });
  }, []);

  return { cancelPreface, requestPreface };
}
